import * as React from "react";
import { Box } from "@mui/material";
import { getAllDashboardData, getAllOwners, getOwnerAndMonthWiseReport } from "state/apis";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import "./index.css";
import { TextField, Grid } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import moment from "moment";
import DefaultProperty from "components/DefaultProperty";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import Autocomplete from "@mui/material/Autocomplete";
import { formatAmount } from "utils/constants";

const OwnerAndMonthReport = () => {
    const propertyName = useSelector((state) => state.global.propertyName);
    const property = useSelector((state) => state.global.defaultProperty);
    const [isLoading, setIsLoading] = React.useState(false);
    const [data, setData] = React.useState({});
    const [openDefaultProperty, setOpenDefaultProperty] = React.useState(false);
    const [owners, setOwners] = React.useState([]);
    const start = moment().startOf("month").format("YYYY-MM-DD");
    const end = moment().endOf("month").format("YYYY-MM-DD");
    const currentYear = moment().format("YYYY");
    const [allProperty, setAllProperty] = React.useState([]);

    const [filter, setFilter] = React.useState({
        startDate: start,
        endDate: end,
        monthValue: currentYear,
        ownerId: null,
        ownerName: "",
        totalNet: 0,
        propertyId: null
    });

    React.useEffect(() => {
        setIsLoading(true);
        fetchOwners();
        if (!property) {
            setOpenDefaultProperty(true);
        }
        setIsLoading(false);
        fetchProperty();
    }, []);

    React.useEffect(() => {
        const { ownerId } = filter;

        setIsLoading(true);
        if (ownerId !== null) {
            fetchData();
        } else {
            setData([]);
            setIsLoading(false);
        }
    }, [filter.ownerId, filter.monthValue, filter.propertyId]);

    const fetchData = async () => {
        try {
            const res = await getOwnerAndMonthWiseReport({
                ownerId: filter.ownerId,
                year: filter.monthValue,
                propertyId: filter.propertyId,
            });
            setData(res.data);
            setIsLoading(false);
            const totalNet = res?.totalNet || 0;
            setFilter((prevData) => ({
                ...prevData,
                totalNet: totalNet,
            }));
        } catch (error) {
            console.error("Error fetching data:", error);
            setIsLoading(false);
        }
    };

    const fetchProperty = async () => {
        const res = await getAllDashboardData();
        setAllProperty(res?.data?.properties || []);
    };

    const fetchOwners = async () => {
        try {
            const res = await getAllOwners(100000, 0);
            setOwners(res?.owners);
        } catch (error) {
            console.error("Error fetching owners:", error);
            toast.error("Failed to fetch owners. Please try again.");
        }
    };

    const handleOwnerChange = (newValue) => {
        setFilter((prevData) => ({
            ...prevData,
            ownerId: newValue ? newValue.id : null,
            ownerName: newValue ? newValue.name : "",
        }));
    };

    const handlePropertyChange = (newValue) => {
        setFilter((prevData) => ({
            ...prevData,
            propertyId: newValue ? newValue.id : null,
        }));
    };

    const handleYearChange = (date) => {
        if (!date) return;
        const selectedYear = dayjs(date).format("YYYY");
        setFilter((prevData) => ({
            ...prevData,
            monthValue: selectedYear,
        }));
    };

    const handleCloseDefaultProperty = () => {
        setOpenDefaultProperty(false);
    };

    const reportRef = React.useRef();

    const handlePrint = () => {
        const input = reportRef.current;

        html2canvas(input, { scale: 2 }).then((canvas) => {
            const imgData = canvas.toDataURL("image/png");
            const pdf = new jsPDF("p", "mm", "a4");

            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = pdf.internal.pageSize.getHeight();

            const margin = 10; // Add margin (padding)

            // Get the image height and scale it proportionally
            const imgProps = pdf.getImageProperties(imgData);
            const imgHeight = (imgProps.height * (pdfWidth - 2 * margin)) / imgProps.width;

            let heightLeft = imgHeight;
            let position = margin;

            pdf.addImage(imgData, "PNG", margin, position, pdfWidth - 2 * margin, imgHeight);
            heightLeft -= pdfHeight - 2 * margin;

            while (heightLeft > 0) {
                position = heightLeft - imgHeight + margin;
                pdf.addPage();
                pdf.addImage(imgData, "PNG", margin, position, pdfWidth - 2 * margin, imgHeight);
                heightLeft -= pdfHeight - 2 * margin;
            }

            pdf.save(`${propertyName}-income-and-expense.pdf`);
        });
    };

    let incomeTotal = 0;
    let expenseTotal = 0;
    return (
        <Box m="1.5rem 2.5rem" mb={5}>
            <Box display="flex" justifyContent="flex-end" pt={0} paddingLeft={0}>
                <Grid container spacing={1} p={"5px"} alignItems="center">
                    <Grid item xs={3}>
                        <Autocomplete
                            disablePortal
                            id="combo-box-demo"
                            options={owners}
                            getOptionLabel={(option) => option.name}
                            value={
                                filter.ownerId
                                    ? owners.find((owner) => owner.id === filter.ownerId)
                                    : null
                            }
                            onChange={(event, newValue) => handleOwnerChange(newValue)}
                            isOptionEqualToValue={(option, value) => option === value}
                            renderOption={(props, option) => (
                                <li {...props} key={option.id}>
                                    {option.name}
                                </li>
                            )}
                            sx={{
                                width: "100%",
                                "& .MuiOutlinedInput-root": {
                                    "&.Mui-focused fieldset": {
                                        borderColor: "primary.main",
                                    },
                                },
                                "& .MuiInputLabel-root": {
                                    "&.Mui-focused": {
                                        color: "primary.main",
                                    },
                                },
                            }}
                            renderInput={(params) => (
                                <TextField {...params} fullWidth label="Select Owner" />
                            )}
                        />
                    </Grid>
                    <Grid item xs={3}>
                        <Autocomplete
                            disablePortal
                            id="combo-box-property"
                            options={allProperty}
                            getOptionLabel={(option) => option.propertyName}
                            value={
                                filter.propertyId
                                    ? allProperty.find(
                                            (property) => property.id === filter.propertyId
                                        )
                                    : null
                            }
                            sx={{
                                width: "100%",
                                "& .MuiOutlinedInput-root": {
                                    "&.Mui-focused fieldset": {
                                        borderColor: "primary.main",
                                    },
                                },
                                "& .MuiInputLabel-root": {
                                    "&.Mui-focused": {
                                        color: "primary.main",
                                    },
                                },
                            }}
                            onChange={(event, newValue) => handlePropertyChange(newValue)}
                            renderInput={(params) => (
                                <TextField {...params} fullWidth label="Select Property" />
                            )}
                        />
                    </Grid>

                    <Grid item xs={2}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                label={"Year"}
                                views={["year"]}
                                name="year"
                                onChange={handleYearChange}
                                value={filter.monthValue ? dayjs(filter.monthValue) : null}
                                sx={{
                                    width: "100%",
                                    "& .MuiOutlinedInput-root": {
                                        "&.Mui-focused fieldset": {
                                            borderColor: "primary.main",
                                        },
                                    },
                                    "& .MuiInputLabel-root": {
                                        "&.Mui-focused": {
                                            color: "primary.main",
                                        },
                                    },
                                }}
                            />
                        </LocalizationProvider>
                    </Grid>

                    <Grid item xs></Grid>
                    <Grid item xs="auto">
                        <button className="download-btn" onClick={handlePrint}>
                            Download PDF
                        </button>
                    </Grid>
                </Grid>
            </Box>
            {!property && (
                <DefaultProperty
                    open={openDefaultProperty}
                    onClose={() => handleCloseDefaultProperty}
                />
            )}
            {filter.ownerId && (
                <div className="report-container">
                    <div ref={reportRef}>
                        <table border="1" cellPadding="5" cellSpacing="0">
                            <thead>
                                <tr>
                                    <th colSpan={5} style={{ textAlign: "center" }}>
                                        {filter?.ownerName}
                                    </th>
                                </tr>
                                <tr>
                                    <th colSpan={5} style={{ textAlign: "center" }}>
                                        {`${filter.monthValue} -INCOME & EXPENDITURE REPORT`}
                                    </th>
                                </tr>
                                <tr>
                                    <th style={{ textAlign: "center" }}>Month</th>
                                    <th style={{ textAlign: "center" }}>PROPERTY</th>
                                    <th style={{ textAlign: "center" }}>Income</th>
                                    <th style={{ textAlign: "center" }}>Expense</th>
                                    <th style={{ textAlign: "center" }}>Net</th>
                                </tr>
                            </thead>

                            <tbody>
                                {data && data.length ? (
                                    <>
                                        {data.map((val, index) => {
                                            incomeTotal += parseFloat(val.income);
                                            expenseTotal += parseFloat(val.expense);
                                            return (
                                                <tr key={index}>
                                                    <td>
                                                        {moment(val.transactionMonth).format(
                                                            "MMM YYYY"
                                                        )}
                                                    </td>
                                                    <td>{val.propertyName}</td>
                                                    <td style={{ textAlign: "right" }}>
                                                        {formatAmount(Number(val.income))}
                                                    </td>
                                                    <td style={{ textAlign: "right" }}>
                                                        {formatAmount(Number(val.expense))}
                                                    </td>
                                                    <td style={{ textAlign: "right" }}>
                                                        {formatAmount(Number(val.net))}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </>
                                ) : (
                                    <tr>
                                        <td colSpan="4" style={{ textAlign: "center" }}>
                                            No data found
                                        </td>
                                    </tr>
                                )}
                                <tr>
                                    <td></td>
                                    <td style={{ fontWeight: "bold" }}>Total:</td>
                                    <td style={{ textAlign: "right" }}>
                                        {formatAmount(Number(incomeTotal))}
                                    </td>
                                    <td style={{ textAlign: "right" }}>
                                        {formatAmount(Number(expenseTotal))}
                                    </td>
                                    <td style={{ textAlign: "right" }}>
                                        {formatAmount(Number(filter?.totalNet))}
                                    </td>
                                </tr>
                                <tr>
                                    <td colSpan={5}></td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </Box>
    );
};

export default OwnerAndMonthReport;
